import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaFlask, FaMicroscope, FaCube, FaTimes, FaShoppingCart, FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useSearchApi from "../../services/Navbar/useSearchApi";
import { useCartActions } from "../../hooks/useCartActions";
import { usePackageCartActions } from "../../hooks/usePackageCartActions";

export default function SearchBar() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  const navigate = useNavigate();
  const { results = [], loading } = useSearchApi(searchTerm);
  const { addWithReplaceCheck, removeItem } = useCartActions();
  const { addPackage, removePackage } = usePackageCartActions();
  const cart = useSelector((state) => state.cart.items || []);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const placeholders = ["Search for tests...", "Find labs near you...", "Explore health packages..."];

  // Rotate placeholder
  useEffect(() => {
    if (searchTerm) return;
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [searchTerm]);

  // Click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔥 Get label based on group type
  const getLabel = (gType) => {
    if (gType === 1) return "Lab";
    if (gType === 2) return "Test";
    if (gType === 3) return "Package";
    return "";
  };

  // 🔥 FIXED: Correct route mapping
  const handleSelect = (item) => {
    console.log("🔍 Item selected:", item);
    
    // Get group type
    let groupType = parseInt((item.groupType ?? "").toString().trim(), 10);
    
    // If name contains "package", treat as package
    if (item.name?.toLowerCase().includes("package")) {
      groupType = 3;
    }

    // Get the ID
    const itemId = item.id || item.TestID || item.testID || item.packageId;
    
    console.log(`📌 GroupType: ${groupType}, ID: ${itemId}`);

    // 🔥 FIXED: Correct route paths based on your project
    const routeMap = {
      1: "Labdetails",    // Lab details page
      2: "TestDetail",    // Test details page (matches your route)
      3: "Packages",      // Packages page (matches your route)
    };
    
    const route = routeMap[groupType];
    
    if (!itemId || !route) {
      console.warn("⚠️ Missing ID or route:", { itemId, route, groupType });
      return;
    }

    // Close dropdown
    setShowDropdown(false);
    setSearchTerm("");
    setIsFocused(false);

    // 🔥 Navigate with correct path
    const navigationPath = `/${route}/${itemId}`;
    console.log(`🚀 Navigating to: ${navigationPath}`);
    navigate(navigationPath);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  // ✅ Price extraction
  const getItemPrice = (item) => {
    return Number(item.packagePrice) ||
           Number(item.price) ||
           Number(item.sellingPrice) ||
           Number(item.SellingPrice) ||
           Number(item.TestPrice) ||
           Number(item.testPrice) ||
           0;
  };

  // ✅ Check if item is in cart
  const isInCart = (productId, categoryId) => {
    return cart.some(
      (item) => Number(item.productId) === Number(productId) && Number(item.categoryId) === Number(categoryId)
    );
  };

  // ✅ Build cart item
  const buildCartItem = (item, price) => {
    const groupType = parseInt(item.groupType, 10);
    const categoryId = groupType === 1 ? 1 : groupType === 2 ? 2 : 3;
    const finalPrice = price || getItemPrice(item);
    return {
      productId: item.id || item.TestID || item.testID || item.packageId,
      categoryId: categoryId,
      name: item.name || item.TestName || item.testName,
      price: finalPrice,
      quantity: 1,
      storeId: item.labId || item.storeId || item.LabId || 0,
      originalPrice: finalPrice,
      discount: 0,
    };
  };

  // ========== CART ACTION ==========
  const handleCartAction = (item, price, e) => {
    e.stopPropagation();
    e.preventDefault();

    const groupType = parseInt(item.groupType, 10);
    const categoryId = groupType === 1 ? 1 : groupType === 2 ? 2 : 3;
    const productId = item.id || item.TestID || item.testID || item.packageId;
    const finalPrice = price || getItemPrice(item);
    const storeId = item.labId || item.storeId || item.LabId || 0;
    const itemName = item.name || item.TestName || item.testName || "";

    const inCart = isInCart(productId, categoryId);

    if (inCart) {
      const removePayload = {
        productId: productId,
        categoryId: categoryId,
        price: finalPrice,
        storeId: storeId,
        productName: itemName,
      };
      if (groupType === 3) {
        removePackage(removePayload);
      } else {
        removeItem(removePayload);
      }
    } else {
      if (groupType === 3) {
        const addPayload = {
          productId: productId,
          categoryId: categoryId,
          price: finalPrice,
          storeId: storeId,
          productName: itemName,
        };
        addPackage(addPayload);
      } else {
        const addPayload = buildCartItem(item, finalPrice);
        addWithReplaceCheck(addPayload);
      }
    }
  };

  const getTypeIcon = (type) => {
    if (type === "Lab") return <FaMicroscope className="text-blue-500" size={14} />;
    if (type === "Test") return <FaFlask className="text-green-500" size={14} />;
    if (type === "Package") return <FaCube className="text-purple-500" size={14} />;
    return null;
  };

  const getTypeColor = (type) => {
    if (type === "Lab") return "bg-blue-50 text-blue-700 border-blue-200";
    if (type === "Test") return "bg-green-50 text-green-700 border-green-200";
    if (type === "Package") return "bg-purple-50 text-purple-700 border-purple-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto" ref={dropdownRef}>
      {/* Flipkart-Style Search Input with Red Theme */}
      <div
        className={`relative flex items-center bg-[#fdfaf8] hover:bg-red-50/40 focus-within:bg-white rounded-lg transition-all duration-200 border ${
          isFocused
            ? "border-red-500 shadow-[0_0_0_2px_rgba(239,68,68,0.15)]"
            : "border-gray-200/80 shadow-xs"
        }`}
      >
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
          <FaSearch className={`text-sm transition-colors ${isFocused ? "text-red-500" : "text-gray-400"}`} />
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholders[placeholderIndex]}
          className="flex-1 bg-transparent outline-none text-xs sm:text-sm text-gray-800 pl-10 pr-10 py-2.5 rounded-lg transition-all placeholder:text-gray-400 font-normal"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => {
            setIsFocused(true);
            if (searchTerm.trim() !== "") setShowDropdown(true);
          }}
          onBlur={() => {
            setTimeout(() => {
              setShowDropdown(false);
              setIsFocused(false);
            }, 200);
          }}
        />
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 p-1 rounded-full"
          >
            <FaTimes size={12} />
          </button>
        )}
      </div>

      {/* Dropdown results */}
      {showDropdown && searchTerm.trim() !== "" && (
        <div className="absolute w-full mt-1.5 bg-white rounded-xl shadow-xl z-50 max-h-[420px] overflow-y-auto border border-gray-100 transition-all duration-150 animate-fadeIn">
          {loading ? (
            <div className="flex items-center justify-center p-5 gap-2.5">
              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-xs text-gray-500 font-medium">Searching for tests & packages...</span>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-6 text-gray-400">
              <FaSearch className="text-2xl mb-2 opacity-30 text-gray-400" />
              <span className="text-xs font-semibold text-gray-600">No matching lab tests or packages</span>
              <span className="text-[11px] text-gray-400 mt-0.5">Try searching for 'Blood Test', 'Thyroid', 'Full Body'</span>
            </div>
          ) : (
            <div className="py-1">
              {results.map((item) => {
                // 🔥 Get group type
                let gType = parseInt((item.groupType ?? "").toString().trim(), 10);
                if (item.name?.toLowerCase().includes("package")) gType = 3;
                
                const itemType = getLabel(gType);
                const productId = item.id || item.TestID || item.testID || item.packageId;
                const categoryId = gType === 1 ? 1 : gType === 2 ? 2 : 3;
                const inCart = isInCart(productId, categoryId);
                const finalPrice = getItemPrice(item);
                const displayName = item.name || item.TestName || item.testName || "";

                return (
                  <div
                    key={item.id || item.TestID || item.testID || item.packageId || Math.random()}
                    className="group flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-red-50/50 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
                    onClick={() => {
                      console.log("👆 Item clicked:", item);
                      handleSelect(item);
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-500">
                        {getTypeIcon(itemType)}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-gray-800 truncate group-hover:text-red-600 transition-colors">
                          {displayName}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {finalPrice > 0 && (
                            <span className="text-xs font-bold text-gray-900">
                              ₹{finalPrice}
                            </span>
                          )}
                          <span
                            className={`text-[9px] font-semibold px-2 py-0.5 rounded-full border ${getTypeColor(
                              itemType
                            )}`}
                          >
                            {itemType}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={(e) => handleCartAction(item, finalPrice, e)}
                      className={`flex items-center gap-1 flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                        inCart
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-xs"
                      }`}
                    >
                      {inCart ? (
                        <>
                          <FaCheck size={9} />
                          <span>Added</span>
                        </>
                      ) : (
                        <>
                          <FaShoppingCart size={9} />
                          <span>Add</span>
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.15s ease-out forwards;
        }
        .overflow-y-auto::-webkit-scrollbar {
          width: 5px;
        }
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f8fafc;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}